import { useState } from "react";
import mascotBadger from "../assets/mascot-badger.png";
import { SignInDialog } from "../components/SignInDialog";

/**
 * Logged-out welcome / splash page (feature 011). Full-viewport, isolated from
 * the app shell (no header/nav). Recreates the design handoff defaults
 * (confident voice, amber accent, racing motif on, medium mascot, subtle
 * disclaimer) from the project's CSS tokens — the prototype HTML/scaffolding is
 * intentionally NOT ported. The single "Sign in" CTA opens the sign-in dialog.
 */
export function WelcomePage() {
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <div className="welcome">
      <div className="welcome-bg" aria-hidden />

      <main className="welcome-main">
        <div className="welcome-hero">
          <div className="welcome-copy">
            <span className="welcome-eyebrow">
              <span className="welcome-eyebrow-dot" aria-hidden />
              MULTI-LEAGUE MODEL LAB · INVITE-ONLY
            </span>

            <h1 className="welcome-headline">
              Your hunch, <span className="welcome-accent">on the record.</span>
            </h1>

            <div className="welcome-lede">
              <p>
                Predabet builds professional-grade models across select sports
                and leagues. Each league has its own custom model — and each one
                becomes a shared baseline you can challenge with a model of your
                own.
              </p>
              <p>
                Reweight the feature groups, train, and score on{" "}
                <strong>held-out future games</strong> — the only games that
                count. See <strong>Baseline vs. My Model</strong> side by side
                and find out if your read actually holds up.
              </p>
            </div>

            <p className="welcome-badger">
              {
                "The honey badger doesn't care about your hunch. It cares about log loss."
              }
            </p>

            <div className="welcome-leagues">
              <span className="welcome-leagues-label">LEAGUES</span>
              <span
                className="welcome-league welcome-league--live"
                data-testid="welcome-league-live"
              >
                <span className="welcome-league-dot" aria-hidden />
                WNBA · live
              </span>
              <span className="welcome-league welcome-league--soon">
                {"More sports & leagues to come"}
              </span>
            </div>

            <div className="welcome-cta-row">
              <button
                type="button"
                className="btn-primary welcome-cta"
                onClick={() => setAuthOpen(true)}
              >
                Sign in
              </button>
            </div>

            <p className="welcome-invite">
              Access is <strong>invite-only</strong>. Predabet does not
              advertise.
            </p>

            <div className="welcome-chips">
              <article className="welcome-chip">
                <h2 className="welcome-chip-title">
                  <span
                    className="welcome-chip-swatch welcome-chip-swatch--indigo"
                    aria-hidden
                  />
                  <span className="welcome-chip-name">Baseline vs. Me</span>
                </h2>
                <p>
                  Every game scored by the shared model and yours, head to head.
                </p>
              </article>
              <article className="welcome-chip">
                <h2 className="welcome-chip-title">
                  <span
                    className="welcome-chip-swatch welcome-chip-swatch--cyan"
                    aria-hidden
                  />
                  <span className="welcome-chip-name">No peeking</span>
                </h2>
                <p>
                  Strict time-split holdouts — evaluated only on games after the
                  cutoff.
                </p>
              </article>
              <article className="welcome-chip">
                <h2 className="welcome-chip-title">
                  <span
                    className="welcome-chip-swatch welcome-chip-swatch--amber"
                    aria-hidden
                  />
                  <span className="welcome-chip-name">Model Lab</span>
                </h2>
                <p>
                  Nudge feature-group weights, retrain, and watch log loss move.
                </p>
              </article>
            </div>
          </div>

          <div className="welcome-mascot">
            <div className="welcome-mascot-card">
              <div className="welcome-mascot-topbar" aria-hidden>
                <span className="welcome-mascot-studio">No Ego Studio</span>
                <span className="welcome-mascot-est">Est. 2026</span>
              </div>
              <div className="welcome-mascot-checker" aria-hidden />
              <img
                className="welcome-mascot-img"
                src={mascotBadger}
                alt="The Predabet honey badger mascot in Ferrari-red racing livery"
              />
              <div className="welcome-mascot-ribbon">
                <span className="welcome-mascot-name">The Honey Badger</span>
                <span className="welcome-mascot-num">#44</span>
              </div>
            </div>
            <div className="welcome-mascot-caption">
              <span className="welcome-mascot-caption-title">
                WELCOME TO THE DEN
              </span>
              <span className="welcome-mascot-caption-sub">
                Fearless about the math. No ego about the picks.
              </span>
            </div>
          </div>
        </div>
      </main>

      <footer className="welcome-footer">
        <p className="welcome-disclaimer">
          Predabet is a personal analytics project for entertainment and
          educational use. Model outputs are probabilistic, not betting advice,
          with no guarantee of accuracy or profit, and nothing here is financial
          advice. If you choose to wager, do so legally and responsibly.{" "}
          <strong>21+</strong> · © 2026 No Ego Studio.
        </p>
      </footer>

      {authOpen ? <SignInDialog onClose={() => setAuthOpen(false)} /> : null}
    </div>
  );
}
